import { useState } from 'react';
import { useEffectOnce } from 'react-use';
import { useDidUpdate } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useDialog } from '@zep/dialog';
import { PlusIcon, XSmallCircleFillIcon } from '@zep/icons';
import { Button, TextField } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { StoreLocales } from '../../../../consts';
import { AllLanguageType } from '../../../../types/StoreTypes';
import S from './UploadLocalesSelection.module.scss';
const UploadLocalesSelection = (props: AssetUploadLanguagesProps) => {
  const {
    initializedLocales,
    isLocaleFormDirties,
    onChange
  } = props;
  const allLocales = [...StoreLocales];
  const {
    t
  } = useTranslation(['common', 'store']);
  const {
    locale
  } = useRouter();
  const dialog = useDialog();
  const [selectedLocales, setSelectedLocales] = useState<Array<string>>([]);
  const [candidateLocales, setCandidateLocales] = useState<Array<string>>([]);
  const [savedLocales, setSavedLocales] = useState<Array<string>>((initializedLocales && [...initializedLocales]) ?? []);
  const initialize = () => {
    selectedLocales.splice(0);
    candidateLocales.splice(0);

    // 선택된 값 세팅
    if (!initializedLocales || initializedLocales.length === 0) {
      // 초기값이 없는 경우 [브라우저 언어, 'en'] 을 기본값으로 세팅
      if (locale != 'en') {
        // @ts-ignore
        selectedLocales.unshift(locale);
      }
      selectedLocales.push('en');
    } else {
      initializedLocales.forEach(value => selectedLocales.push(value));
    }

    // 선택되지 않은 값들은 후보값으로 세팅
    allLocales.forEach(value => {
      if (!selectedLocales.includes(value)) {
        candidateLocales.push(value);
      }
    });
    setSelectedLocales([...selectedLocales]);
    setCandidateLocales([...candidateLocales]);
  };

  /**
   * 언어값 추가
   * @param language
   * @param index
   */
  const handleAdd = (language: string, index: number) => {
    if (language === locale) {
      selectedLocales.unshift(language);
    } else {
      selectedLocales.push(language);
    }
    candidateLocales.splice(index, 1);
    setSelectedLocales([...selectedLocales]);
    setCandidateLocales([...candidateLocales]);
  };

  /**
   * 언어값 삭제
   * @param language
   * @param index
   */
  const handleDelete = async (language: string, index: number) => {
    const savedLocaleIndex = savedLocales.indexOf(language);
    const isAllowDelete = savedLocaleIndex > -1 || isLocaleFormDirties && isLocaleFormDirties[language] ? await dialog.confirm({
      title: t('store:store_display_language_delete_description'),
      confirmVariant: 'alert',
      confirmText: t('store:store_delete')
    }) : true;
    if (isAllowDelete) {
      selectedLocales.splice(index, 1);
      candidateLocales.unshift(language);
      setSelectedLocales([...selectedLocales]);
      setCandidateLocales([...candidateLocales]);
      if (savedLocaleIndex > -1) {
        savedLocales.splice(savedLocaleIndex, 1);
        setSavedLocales([...savedLocales]);
      }
    }
  };
  useDidUpdate(() => {
    onChange && onChange(selectedLocales);
  }, [selectedLocales]);
  useEffectOnce(() => {
    initialize();
  });
  return <div className={S.field} data-sentry-component="UploadLocalesSelection" data-sentry-source-file="UploadLocalesSelection.tsx">
      <TextField label={t('store:store_display_language')} data-sentry-element="TextField" data-sentry-source-file="UploadLocalesSelection.tsx">
        <div className={S.description}>
          {t('store:store_display_language_description')}
        </div>

        <div className={S.language_buttons}>
          {selectedLocales.map((localeId, index) => <Button key={localeId} size={'xs'} variant={'primary'} rounded={true} onClick={() => handleDelete(localeId, index)} rightSlot={selectedLocales.length > 1 && <XSmallCircleFillIcon width={16} height={16} fill={Colors.PrimaryDisabled} />} aria-label="Delete">
              {t(`common:language.${localeId as AllLanguageType}`)}
            </Button>)}

          {candidateLocales.map((localeId, index) => <Button key={localeId} size={'xs'} variant={'sub'} rounded={true} leftSlot={<PlusIcon width={16} height={16} aria-label="Plus" />} onClick={() => handleAdd(localeId, index)}>
              {t(`common:language.${localeId as AllLanguageType}`)}
            </Button>)}
        </div>
      </TextField>
    </div>;
};
export default UploadLocalesSelection;
type AssetUploadLanguagesProps = {
  initializedLocales?: Array<string>;
  onChange?: (selectedLocales: Array<string>) => void;
  isLocaleFormDirties?: Record<string, boolean>;
};